/**
 * Platform API service for project management
 */

import { createApiError } from "./errorHandler";
import type {
  ConversationOut,
  ConversationDetailOut,
  ConversationUpdate,
  ConversationListParams,
  ConversationSummaryResponse,
  UnifiedConversationListParams,
  UnifiedConversationListResponse,
  MessageOut,
  TimeSeriesResponse,
  ActivityResponse,
  TopPagesResponse,
  LocationsResponse,
  SparklineResponse,
} from "@/shared/types/conversations";

import type {
  AccessibleConnectorResourceListResponse,
  CredentialCreateRequest,
  CredentialResponse,
  CredentialListResponse,
  CredentialValidateResponse,
  AccessibleReposResponse,
  ConnectorCreateRequest,
  ConnectorUpdateRequest,
  ConnectorResponse,
  ConnectorListResponse,
  ConnectorRunListResponse,
  SyncTriggerResponse,
} from "@/shared/types/connector";
import type {
  LLMCredentialCreateRequest,
  LLMCredentialListResponse,
  LLMCredentialResponse,
  LLMCredentialValidateResponse,
  LLMProviderModelListResponse,
} from "@/shared/types/llmProvider";
import { PLATFORM_API_URL } from "@/shared/constants/projectConfig";

// Project types
export interface Project {
  uuid: string;
  name: string;
  display_name: string;
  description?: string;
  icon: string;
  color: string;
  image_url?: string;
  project_type: "core" | "custom" | "template";
  is_core: boolean;
  is_active: boolean;
  is_public: boolean;
  created_at: string;
  updated_at: string;
  owner_uuid?: string;
  tokens_in?: number;
  tokens_out?: number;
  tokens_total?: number;
}

export interface ProjectCreate {
  name: string;
  display_name: string;
  description?: string;
  icon: string;
  color: string;
  is_public?: boolean;
}

export interface ProjectUpdate {
  display_name?: string;
  description?: string;
  icon?: string;
  color?: string;
  image_url?: string;
  is_public?: boolean;
}

export interface ProjectListResponse {
  projects: Project[];
  total: number;
  page: number;
  per_page: number;
}

export interface ProjectStats {
  project_id: number;
  project_name: string;
  data_sources_count: number;
  total_characters: number;
  last_activity?: string;
}

export interface DashboardData {
  project_uuid: string;
  project_name: string;
  total_sources: number;
  sources_by_status: {
    pending: number;
    processing: number;
    processed: number;
    error: number;
  };
  sources_by_type: {
    website: number;
    sitemap: number;
    url_list: number;
    text: number;
    faq: number;
    document: number;
  };
  total_characters: number;
  total_file_size: number;
  recent_sources: Array<{
    uuid: string;
    name: string;
    source_type: string;
    status: string;
    created_at: string | null;
    character_count: number | null;
    error_message: string | null;
    processing_metadata?: {
      preview?: string;
      faq_count?: number;
      filename?: string;
      [key: string]: any;
    };
  }>;
  last_indexed_at?: string | null;
  kb_status?: "empty" | "syncing" | "successful" | "mixed" | "failed";
}

/** What a gateway model *is*, beyond its name. "router" models (LiteLLM
 *  auto_router) dispatch a turn to one of several backing models; the internal
 *  "pool" kind is filtered out server-side and never reaches the client. */
export type AvailableModelKind = "plain" | "pool" | "router";

export interface AvailableModel {
  name: string;
  provider: string;
  display_name: string;
  kind?: AvailableModelKind;
  size?: number | null;
  details?: Record<string, unknown>;
  modified_at?: string | null;
  available: boolean;
  max_tokens?: number | null;
  /** Per-token USD prices from the gateway price catalog; null when the gateway
   *  has no price configured for the model. */
  input_cost_per_token?: number | null;
  output_cost_per_token?: number | null;
  metadata?: Record<string, unknown>;
  last_checked?: string;
}

class PlatformApiService {
  private readonly baseUrl: string;

  constructor() {
    this.baseUrl = PLATFORM_API_URL;
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {},
  ): Promise<T> {
    const url = `${this.baseUrl}/api${endpoint}`;

    const config: RequestInit = {
      headers: {
        "Content-Type": "application/json",
        ...options.headers,
      },
      credentials: "include", // BFF session cookie carries auth
      ...options,
    };

    const response = await fetch(url, config);

    if (!response.ok) {
      // Use centralized error creation
      const apiError = await createApiError(response, endpoint);
      throw apiError;
    }

    // 204 No Content and empty bodies can't be parsed as JSON. This matters
    // for DELETE endpoints that return 204 — previously this threw a spurious
    // JSON parse error even though the server call succeeded.
    if (response.status === 204) {
      return undefined as T;
    }

    const text = await response.text();
    if (!text) {
      return undefined as T;
    }

    return JSON.parse(text) as T;
  }

  // Project Management
  async getProjects(params?: {
    page?: number;
    per_page?: number;
    include_inactive?: boolean;
    project_type?: string;
  }): Promise<ProjectListResponse> {
    const searchParams = new URLSearchParams();

    if (params?.page) searchParams.set("page", params.page.toString());
    if (params?.per_page)
      searchParams.set("per_page", params.per_page.toString());
    if (params?.include_inactive) searchParams.set("include_inactive", "true");
    if (params?.project_type)
      searchParams.set("project_type", params.project_type);

    const queryString = searchParams.toString();
    const endpoint = queryString ? `/projects?${queryString}` : "/projects";

    return this.request<ProjectListResponse>(endpoint);
  }

  async getProject(projectUUID: string): Promise<Project> {
    return this.request<Project>(`/projects/${projectUUID}`);
  }

  async createProject(projectData: ProjectCreate): Promise<Project> {
    return this.request<Project>("/projects", {
      method: "POST",
      body: JSON.stringify(projectData),
    });
  }

  async checkProjectName(name: string): Promise<{ available: boolean }> {
    return this.request<{ available: boolean }>(
      `/projects/check-name?name=${encodeURIComponent(name)}`,
    );
  }

  async updateProject(
    projectUUID: string,
    projectData: ProjectUpdate,
  ): Promise<Project> {
    return this.request<Project>(`/projects/${projectUUID}`, {
      method: "PUT",
      body: JSON.stringify(projectData),
    });
  }

  // --- UPDATED METHOD ---
  async deleteProject(
    projectUUID: string,
    force: boolean = false,
  ): Promise<{ message: string }> {
    // Append query parameter if force is true
    const endpoint = force
      ? `/projects/${projectUUID}?force=true`
      : `/projects/${projectUUID}`;

    return this.request<{ message: string }>(endpoint, {
      method: "DELETE",
    });
  }
  // -----------------------

  async getProjectStats(projectUUID: string): Promise<ProjectStats> {
    return this.request<ProjectStats>(`/projects/${projectUUID}/stats`);
  }

  async getProjectDashboard(projectUUID: string): Promise<DashboardData> {
    return this.request<DashboardData>(`/projects/${projectUUID}/dashboard`);
  }

  // Legacy compatibility - get available project names
  async getAvailableProjects(): Promise<{
    status: string;
    projects: string[];
  }> {
    return this.request<{ status: string; projects: string[] }>(
      "/projects/available",
    );
  }

  async getAvailableModels(forceRefresh = false): Promise<AvailableModel[]> {
    const searchParams = new URLSearchParams();

    if (forceRefresh) {
      searchParams.set("force_refresh", "true");
    }

    const queryString = searchParams.toString();
    const endpoint = queryString
      ? `/models/available?${queryString}`
      : "/models/available";

    return this.request<AvailableModel[]>(endpoint);
  }

  async createLlmProviderCredential(
    projectUUID: string,
    data: LLMCredentialCreateRequest,
  ): Promise<LLMCredentialResponse> {
    return this.request<LLMCredentialResponse>(
      `/llm-provider-credentials/projects/${projectUUID}`,
      { method: "POST", body: JSON.stringify(data) },
    );
  }

  async getLlmProviderCredentials(
    projectUUID: string,
  ): Promise<LLMCredentialListResponse> {
    return this.request<LLMCredentialListResponse>(
      `/llm-provider-credentials/projects/${projectUUID}`,
    );
  }

  async validateLlmProviderCredential(
    credentialUUID: string,
  ): Promise<LLMCredentialValidateResponse> {
    return this.request<LLMCredentialValidateResponse>(
      `/llm-provider-credentials/${credentialUUID}/validate`,
      { method: "POST" },
    );
  }

  async getLlmProviderModels(
    credentialUUID: string,
  ): Promise<LLMProviderModelListResponse> {
    return this.request<LLMProviderModelListResponse>(
      `/llm-provider-credentials/${credentialUUID}/models`,
    );
  }

  async deleteLlmProviderCredential(credentialUUID: string): Promise<void> {
    await this.request<void>(`/llm-provider-credentials/${credentialUUID}`, {
      method: "DELETE",
    });
  }

  // Conversation stats
  async getConversationStats(
    projectUUID: string,
  ): Promise<ConversationSummaryResponse> {
    return this.request<ConversationSummaryResponse>(
      `/conversations/stats/summary?project_uuid=${encodeURIComponent(projectUUID)}`,
    );
  }

  // Conversation CRUD
  async getConversations(
    params?: ConversationListParams,
  ): Promise<ConversationOut[]> {
    const searchParams = new URLSearchParams();

    if (params?.project_uuid)
      searchParams.set("project_uuid", params.project_uuid);
    if (params?.session_id) searchParams.set("session_id", params.session_id);
    if (params?.status) searchParams.set("status", params.status);
    if (params?.search) searchParams.set("search", params.search);
    if (params?.date_from) searchParams.set("date_from", params.date_from);
    if (params?.date_to) searchParams.set("date_to", params.date_to);
    if (params?.limit != null)
      searchParams.set("limit", params.limit.toString());
    if (params?.offset != null)
      searchParams.set("offset", params.offset.toString());

    const queryString = searchParams.toString();
    const endpoint = queryString
      ? `/conversations?${queryString}`
      : "/conversations";

    return this.request<ConversationOut[]>(endpoint);
  }

  async getConversation(
    conversationUUID: string,
  ): Promise<ConversationDetailOut> {
    return this.request<ConversationDetailOut>(
      `/conversations/${conversationUUID}`,
    );
  }

  /** Unified, source-tagged list merging chatbot + agent conversations and
   *  agent-flow runs for the top-level Conversations page. */
  async getUnifiedConversations(
    projectUUID: string,
    params?: UnifiedConversationListParams,
  ): Promise<UnifiedConversationListResponse> {
    const searchParams = new URLSearchParams();
    if (params?.source) searchParams.set("source", params.source);
    if (params?.search) searchParams.set("search", params.search);
    if (params?.date_from) searchParams.set("date_from", params.date_from);
    if (params?.date_to) searchParams.set("date_to", params.date_to);
    if (params?.page != null) searchParams.set("page", params.page.toString());
    if (params?.per_page != null)
      searchParams.set("per_page", params.per_page.toString());
    const qs = searchParams.toString();
    return this.request<UnifiedConversationListResponse>(
      `/projects/${projectUUID}/conversations/unified${qs ? `?${qs}` : ""}`,
    );
  }

  async updateConversation(
    conversationUUID: string,
    data: ConversationUpdate,
  ): Promise<ConversationOut> {
    return this.request<ConversationOut>(`/conversations/${conversationUUID}`, {
      method: "PATCH",
      body: JSON.stringify(data),
    });
  }

  async deleteConversation(conversationUUID: string): Promise<void> {
    await this.request<void>(`/conversations/${conversationUUID}`, {
      method: "DELETE",
    });
  }

  async getConversationMessages(
    conversationUUID: string,
  ): Promise<MessageOut[]> {
    return this.request<MessageOut[]>(
      `/conversations/${conversationUUID}/messages`,
    );
  }

  // Conversation analytics
  async getConversationTimeSeries(
    projectUUID: string,
    dateFrom: string,
    dateTo: string,
    granularity: "day" | "week" | "month" = "day",
  ): Promise<TimeSeriesResponse> {
    const params = new URLSearchParams({
      project_uuid: projectUUID,
      date_from: dateFrom,
      date_to: dateTo,
      granularity,
    });
    return this.request<TimeSeriesResponse>(
      `/conversations/stats/timeseries?${params}`,
    );
  }

  async getConversationActivity(
    projectUUID: string,
    dateFrom?: string,
    dateTo?: string,
  ): Promise<ActivityResponse> {
    const params = new URLSearchParams({ project_uuid: projectUUID });
    if (dateFrom) params.set("date_from", dateFrom);
    if (dateTo) params.set("date_to", dateTo);
    return this.request<ActivityResponse>(
      `/conversations/stats/activity?${params}`,
    );
  }

  async getConversationTopPages(
    projectUUID: string,
    dateFrom?: string,
    dateTo?: string,
    limit: number = 10,
  ): Promise<TopPagesResponse> {
    const params = new URLSearchParams({
      project_uuid: projectUUID,
      limit: String(limit),
    });
    if (dateFrom) params.set("date_from", dateFrom);
    if (dateTo) params.set("date_to", dateTo);
    return this.request<TopPagesResponse>(
      `/conversations/stats/top-pages?${params}`,
    );
  }

  async getConversationLocations(
    projectUUID: string,
    dateFrom?: string,
    dateTo?: string,
  ): Promise<LocationsResponse> {
    const params = new URLSearchParams({ project_uuid: projectUUID });
    if (dateFrom) params.set("date_from", dateFrom);
    if (dateTo) params.set("date_to", dateTo);
    return this.request<LocationsResponse>(
      `/conversations/stats/locations?${params}`,
    );
  }

  async getConversationSparklines(
    days: number = 7,
  ): Promise<SparklineResponse> {
    return this.request<SparklineResponse>(
      `/conversations/stats/sparklines?days=${days}`,
    );
  }

  // ========================================
  // Connector Credentials
  // ========================================

  async createCredential(
    projectUUID: string,
    data: CredentialCreateRequest,
  ): Promise<CredentialResponse> {
    return this.request<CredentialResponse>(
      `/connectors/credentials/projects/${projectUUID}`,
      { method: "POST", body: JSON.stringify(data) },
    );
  }

  async getCredentials(projectUUID: string): Promise<CredentialListResponse> {
    return this.request<CredentialListResponse>(
      `/connectors/credentials/projects/${projectUUID}`,
    );
  }

  async validateCredential(
    credentialUUID: string,
  ): Promise<CredentialValidateResponse> {
    return this.request<CredentialValidateResponse>(
      `/connectors/credentials/${credentialUUID}/validate`,
      { method: "POST" },
    );
  }

  async deleteCredential(credentialUUID: string): Promise<void> {
    await this.request<void>(`/connectors/credentials/${credentialUUID}`, {
      method: "DELETE",
    });
  }

  async getAccessibleRepos(
    credentialUUID: string,
  ): Promise<AccessibleReposResponse> {
    return this.request<AccessibleReposResponse>(
      `/connectors/credentials/${credentialUUID}/repos`,
    );
  }

  async getAccessibleResources(
    credentialUUID: string,
    parentId?: string | null,
  ): Promise<AccessibleConnectorResourceListResponse> {
    const path = parentId
      ? `/connectors/credentials/${credentialUUID}/resources?parent_id=${encodeURIComponent(parentId)}`
      : `/connectors/credentials/${credentialUUID}/resources`;
    return this.request<AccessibleConnectorResourceListResponse>(path);
  }

  getConnectorOAuthStartUrl(
    connectorType:
      | "google_drive"
      | "gmail"
      | "google_calendar"
      | "google_sheets"
      | "notion"
      | "github"
      | "gitlab",
    projectUUID: string,
    options?: {
      credentialName?: string;
      frontendOrigin?: string;
    },
  ): string {
    const params = new URLSearchParams({ project_id: projectUUID });

    if (options?.credentialName) {
      params.set("credential_name", options.credentialName);
    }

    if (options?.frontendOrigin) {
      params.set("frontend_origin", options.frontendOrigin);
    }

    // Backend route segments hyphenate the underscore-form connector type.
    const connectorPath = connectorType.replace(/_/g, "-");

    return `${this.baseUrl}/api/connectors/oauth/${connectorPath}/start?${params.toString()}`;
  }

  getGoogleDriveOAuthStartUrl(
    projectUUID: string,
    options?: {
      credentialName?: string;
      frontendOrigin?: string;
    },
  ): string {
    return this.getConnectorOAuthStartUrl("google_drive", projectUUID, options);
  }

  // ========================================
  // Connectors
  // ========================================

  async createConnector(
    projectUUID: string,
    data: ConnectorCreateRequest,
  ): Promise<ConnectorResponse> {
    return this.request<ConnectorResponse>(
      `/connectors/projects/${projectUUID}`,
      { method: "POST", body: JSON.stringify(data) },
    );
  }

  async getConnectors(projectUUID: string): Promise<ConnectorListResponse> {
    return this.request<ConnectorListResponse>(
      `/connectors/projects/${projectUUID}`,
    );
  }

  async getConnector(connectorUUID: string): Promise<ConnectorResponse> {
    return this.request<ConnectorResponse>(`/connectors/${connectorUUID}`);
  }

  async updateConnector(
    connectorUUID: string,
    data: ConnectorUpdateRequest,
  ): Promise<ConnectorResponse> {
    return this.request<ConnectorResponse>(`/connectors/${connectorUUID}`, {
      method: "PUT",
      body: JSON.stringify(data),
    });
  }

  async deleteConnector(connectorUUID: string): Promise<void> {
    await this.request<void>(`/connectors/${connectorUUID}`, {
      method: "DELETE",
    });
  }

  async syncConnector(connectorUUID: string): Promise<SyncTriggerResponse> {
    return this.request<SyncTriggerResponse>(
      `/connectors/${connectorUUID}/sync`,
      { method: "POST" },
    );
  }

  async pauseConnector(connectorUUID: string): Promise<ConnectorResponse> {
    return this.request<ConnectorResponse>(
      `/connectors/${connectorUUID}/pause`,
      { method: "POST" },
    );
  }

  async resumeConnector(connectorUUID: string): Promise<ConnectorResponse> {
    return this.request<ConnectorResponse>(
      `/connectors/${connectorUUID}/resume`,
      { method: "POST" },
    );
  }

  async getConnectorRuns(
    connectorUUID: string,
  ): Promise<ConnectorRunListResponse> {
    return this.request<ConnectorRunListResponse>(
      `/connectors/${connectorUUID}/runs`,
    );
  }

  // ========================================
  // Read Connectors (agent live-read)
  // ========================================

  async getReadConnectors(
    projectUUID: string,
  ): Promise<import("@/shared/types/read-connector").ReadConnectorListResponse> {
    return this.request(
      `/read-connectors/projects/${projectUUID}`,
    );
  }

  async getReadConnectorProviders(): Promise<
    import("@/shared/types/read-connector").ReadConnectorProvidersResponse
  > {
    return this.request(`/read-connectors/providers`);
  }

  async createReadConnector(
    projectUUID: string,
    data: import("@/shared/types/read-connector").ReadConnectorCreateRequest,
  ): Promise<import("@/shared/types/read-connector").ReadConnectorResponse> {
    return this.request(
      `/read-connectors/projects/${projectUUID}`,
      { method: "POST", body: JSON.stringify(data) },
    );
  }

  async updateReadConnector(
    readConnectorUUID: string,
    data: import("@/shared/types/read-connector").ReadConnectorUpdateRequest,
  ): Promise<import("@/shared/types/read-connector").ReadConnectorResponse> {
    return this.request(
      `/read-connectors/${readConnectorUUID}`,
      { method: "PUT", body: JSON.stringify(data) },
    );
  }

  async deleteReadConnector(readConnectorUUID: string): Promise<void> {
    await this.request<void>(`/read-connectors/${readConnectorUUID}`, {
      method: "DELETE",
    });
  }

  async getReadConnectorAudit(
    projectUUID: string,
    options?: { limit?: number; offset?: number },
  ): Promise<import("@/shared/types/read-connector").AgentExternalReadListResponse> {
    const params = new URLSearchParams();
    if (options?.limit !== undefined) params.set("limit", String(options.limit));
    if (options?.offset !== undefined) params.set("offset", String(options.offset));
    const qs = params.toString();
    return this.request(
      `/read-connectors/projects/${projectUUID}/audit${qs ? `?${qs}` : ""}`,
    );
  }

  // Health check
  async healthCheck(): Promise<{
    status: string;
    database: string;
    version: string;
  }> {
    return this.request<{ status: string; database: string; version: string }>(
      "/health",
    );
  }

  // Agent service availability (MCP server + orchestrator).
  // Returns { mcp_server, orchestrator } each with status "online" |
  // "offline" | "not_configured". Used by the agent config UI to dim
  // affordances when a dependency is unreachable.
  async getAgentServiceHealth(): Promise<AgentServiceHealthResponse> {
    return this.request<AgentServiceHealthResponse>("/agent/service-health");
  }
}

export type AgentServiceHealthStatus = "online" | "offline" | "not_configured";

export interface AgentServiceHealthEntry {
  status: AgentServiceHealthStatus;
  url?: string | null;
  detail?: string | null;
}

export interface AgentServiceHealthResponse {
  mcp_server: AgentServiceHealthEntry;
  orchestrator: AgentServiceHealthEntry;
}

// Export singleton instance
export const platformApi = new PlatformApiService();
